// Invert the agents' [[ROUTE_FORM:multi={legs}]] seed into RouteFormMulti's initialLegs shape so the
// form opens PRE-FILLED with the itinerary the customer described in chat (or the latest legs after a
// chat edit), for EVERY trip type: token place {address,city,state,isAirport,iata} -> LocationField
// place {label,city,state,isAirport,iataCode}; the combined departDateTime -> split date + time; a
// leg-1 returnDateTime -> the "return to origin" toggle (roundTrip + split returnDate/returnTime); and
// stops carried through so a confirm doesn't wipe them.
//
// Kept dependency-free (no React / no CustomerApp) so it's unit-testable on its own.
export function mapTokenLegs(tokenLegs) {
  if (!Array.isArray(tokenLegs) || tokenLegs.length === 0) return null;
  return _mapLegs(tokenLegs);
}

// A pre-resolved ambiguity candidate ({address,city,state,isAirport,iata,precision,lat,lon,placeId}
// from the server) -> the LocationField place shape, so picking one needs no re-geocode.
const _toCandidate = (c) => ({
  label: c.address || c.label || '',
  city: c.city || '',
  state: c.state || '',
  isAirport: !!c.isAirport,
  iataCode: c.iata || c.iataCode || null,
  precision: c.precision || null,
  lat: c.lat ?? null,
  lng: c.lon ?? c.lng ?? null,
  placeId: c.placeId || null,
  hasCoords: Number.isFinite(c.lat) && Number.isFinite(c.lon ?? c.lng),
});

// token place {address,city,state,name,isAirport,iata,candidates,…} -> LocationField place shape.
function _toPlace(p) {
  if (!p) return null;
  const candidates = Array.isArray(p.candidates) && p.candidates.length ? p.candidates.map(_toCandidate) : null;
  const withOptional = (base) => {
    if (p.precision) base.precision = p.precision;
    if (candidates) base.candidates = candidates;
    if (p.status) base.status = p.status;
    if (p.descriptive) base.descriptive = true;   // a vague stop kept verbatim ("Lunch stop")
    if (p.name) base.name = p.name;               // venue name (school/hotel/venue) -> Location name field
    return base;
  };
  if (p.address) {
    return withOptional({ label: p.address, city: p.city || '', state: p.state || '',
                          isAirport: !!p.isAirport, iataCode: p.iata || null });
  }
  // no exact address yet -> a coarse (city-level) place. Keep it (not null) when candidates exist.
  if (!p.city && !p.state && !candidates) return null;
  return withOptional({ label: '', city: p.city || '', state: p.state || '', isAirport: false, iataCode: null });
}

const _splitDT = (dt) => {
  const s = dt || '';
  const [date, time = ''] = s.includes('T') ? s.split('T') : [s, ''];
  return [date || '', (time || '').slice(0, 5)];
};

function _mapLegs(tokenLegs) {
  return tokenLegs.map((l) => {
    const [date, time] = _splitDT(l.departDateTime);
    const leg = {
      from: _toPlace(l.pickup),
      to: _toPlace(l.destination),
      departDate: date,
      departTime: time,
      stops: (l.stops || []).map(_toPlace).filter(Boolean),
    };
    if ((l.returnDateTime || '').trim()) {
      const [rdate, rtime] = _splitDT(l.returnDateTime);
      leg.roundTrip = true;
      leg.returnDate = rdate;
      leg.returnTime = rtime;
    }
    return leg;
  });
}

// Invert a ROUND-TRIP seed ({outbound,return,passengerCount,coachDisposition} — from the agents'
// [[ROUTE_FORM:round_trip={…}]] or a quote card's editSeed) into RouteFormRoundTrip's `initial` shape.
// Each leg carries {from,to,departDate,departTime,stops}; the return "to" is the original departure.
export function mapRoundTripSeed(seed) {
  if (!seed || typeof seed !== 'object') return null;
  const mapLeg = (l) => {
    if (!l) return { from: null, to: null, departDate: '', departTime: '', stops: [] };
    const [date, time] = _splitDT(l.departDateTime);
    return {
      from: _toPlace(l.pickup),
      to: _toPlace(l.destination),
      departDate: date,
      departTime: time,
      stops: (l.stops || []).map(_toPlace).filter(Boolean),
    };
  };
  return {
    outbound: mapLeg(seed.outbound),
    return: mapLeg(seed.return),
    passengerCount: seed.passengerCount != null ? String(seed.passengerCount) : '',
    coachDisposition: seed.coachDisposition || '',
  };
}
